import { Receipt } from 'lucide-react';
import { PageHeader } from '@/shared/components/ui/PageHeader';
// ─────────────────────────────────────────────────────────────────────────────
// Services & Projects — Billing Page
// ─────────────────────────────────────────────────────────────────────────────

import { useMemo, useState, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { useQueryClient } from '@tanstack/react-query';
import { servicesProjectsQueryKeys } from '../api';
import {
  DollarSign, Plus, Search, Eye, Edit, Trash2,
  Download, FileText, ReceiptText, CreditCard, Banknote,
  CheckCircle, AlertCircle, RefreshCw, Settings, Send, Bell
} from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { Badge } from '@/shared/components/ui/Badge';
import { Card, CardHeader, CardTitle, CardContent } from '@/shared/components/ui/Card';
import { Modal } from '@/shared/components/ui/Modal';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/shared/components/ui/Table';
import { formatINR } from '@/shared/utils/currency';
import { useProjects, useContracts, useTimesheets, useInvoices } from '../hooks/useServicesProjectsApi';
import { useParties } from '@/features/accounts/hooks/useParties';
import { localToday } from '@/shared/utils/utils';

const INVOICE_STATUSES = [
  { value: 'draft', label: 'Draft', color: 'bg-slate-100 text-slate-700' },
  { value: 'pending_approval', label: 'Pending Approval', color: 'bg-blue-100 text-blue-800' },
  { value: 'approved', label: 'Approved', color: 'bg-emerald-100 text-emerald-800' },
  { value: 'sent', label: 'Sent', color: 'bg-purple-100 text-purple-800' },
  { value: 'paid', label: 'Paid', color: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  { value: 'partial_paid', label: 'Partial', color: 'bg-amber-100 text-amber-800' },
  { value: 'overdue', label: 'Overdue', color: 'bg-red-100 text-red-800' },
  { value: 'cancelled', label: 'Cancelled', color: 'bg-slate-100 text-slate-500' },
];

const BILLING_TYPES = [
  { value: 'milestone', label: 'Milestone Billing' },
  { value: 'time_material', label: 'Time & Material' },
  { value: 'retainer', label: 'Retainer' },
  { value: 'advance', label: 'Advance Billing' },
  { value: 'recurring', label: 'Recurring' },
  { value: 'final', label: 'Final Invoice' },
];

function errorText(err: unknown, fallback: string) {
  const e = err as { response?: { data?: { error?: string } } };
  return e?.response?.data?.error || fallback;
}

function getStatusConfig(status: string) {
  return INVOICE_STATUSES.find(s => s.value === status) || { label: status, color: 'bg-slate-100 text-slate-700' };
}

export function BillingPage() {
  const [activeTab, setActiveTab] = useState<'invoices' | 'generate' | 'schedule' | 'payments'>('invoices');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [projectFilter, setProjectFilter] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [viewing, setViewing] = useState<any>(null);
  const [formData, setFormData] = useState<any>({ items: [] });
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);

  const queryClient = useQueryClient();
  const { data: invoices = [], isLoading: invoicesLoading } = useInvoices();
  const { data: projects = [] } = useProjects();
  const { data: contracts = [] } = useContracts();
  const { data: timesheets = [] } = useTimesheets({ status: 'approved' });
  const { data: clients = [] } = useParties();

  // Type definitions
  interface Invoice {
    _id: string;
    invoiceNumber?: string;
    invoiceDate?: string;
    dueDate?: string;
    clientName?: string;
    projectName?: string;
    projectId?: string;
    clientId?: string;
    billingType?: string;
    status?: string;
    amountMinor?: number;
    paidAmountMinor?: number;
    paidAt?: string;
    items?: any[];
    notes?: string;
  }

  interface Project {
    _id: string;
    name: string;
    status?: string;
    client?: string;
    clientId?: string;
    contractId?: string;
    billingMethod?: string;
  }

  interface Contract {
    _id: string;
    contractNumber?: string;
    clientName?: string;
    totalValueMinor?: number;
  }

  interface Timesheet {
    _id: string;
    projectId: string;
    minutes: number;
    billingRateMinor: number;
    billable: boolean;
  }

  // Filter invoices
  const filteredInvoices = useMemo(() => {
    return invoices.filter((inv: Invoice) => {
      if (search && !inv.invoiceNumber?.toLowerCase().includes(search.toLowerCase()) &&
          !inv.clientName?.toLowerCase().includes(search.toLowerCase()) &&
          !inv.projectName?.toLowerCase().includes(search.toLowerCase())) return false;
      if (statusFilter && inv.status !== statusFilter) return false;
      if (typeFilter && inv.billingType !== typeFilter) return false;
      if (projectFilter && inv.projectId !== projectFilter) return false;
      if (clientFilter && inv.clientId !== clientFilter) return false;
      return true;
    });
  }, [invoices, search, statusFilter, typeFilter, projectFilter, clientFilter]);

  // Compute billable data for generation
  const billableProjects = useMemo(() => {
    return projects.map((p: Project) => {
      const projectTimesheets = timesheets.filter((t: Timesheet) => t.projectId === p._id && t.billable);
      const totalMinutes = projectTimesheets.reduce((sum: number, t: Timesheet) => sum + (t.minutes || 0), 0);
      const totalAmount = projectTimesheets.reduce((sum: number, t: Timesheet) => 
        sum + Math.round((t.minutes || 0) / 60 * (t.billingRateMinor || 0)), 0);
      
      const contract = contracts.find((c: Contract) => c._id === p.contractId);
      const billedInvoices = invoices.filter((i: Invoice) => i.projectId === p._id && i.status !== 'cancelled');
      const billedAmount = billedInvoices.reduce((sum: number, i: Invoice) => sum + (i.amountMinor || 0), 0);
      
      return {
        project: p,
        contract,
        timesheets: projectTimesheets,
        totalMinutes,
        totalHours: (totalMinutes / 60).toFixed(1),
        totalAmount,
        billedAmount,
        remainingAmount: Math.max(0, (contract?.totalValueMinor || totalAmount) - billedAmount),
      };
    }).filter(p => p.project.status === 'active' || p.totalMinutes > 0 || !!p.contract || p.remainingAmount > 0);
  }, [projects, timesheets, contracts, invoices]);

  const resetForm = useCallback(() => {
    setFormData({
      clientId: '',
      projectId: '',
      contractId: '',
      billingType: 'time_material',
      invoiceDate: localToday(),
      dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      currency: 'INR',
      items: [] as any[],
      notes: '',
      terms: '',
    });
    setEditing(null);
    setCreating(true);
  }, []);

  const handleCreate = () => {
    resetForm();
  };

  const handleEdit = (item: Invoice) => {
    setEditing(item);
    setFormData({ ...item, items: item.items || [] });
    setCreating(true);
  };

  const handleView = (item: Invoice) => {
    setViewing(item);
  };

  const addItem = () => {
    setFormData((f: any) => ({ ...f, items: [...(f.items || []), {
      id: Date.now().toString(),
      description: '',
      quantity: 1,
      rateMinor: 0,
      amountMinor: 0,
    }] }));
  };

  const removeItem = (id: string) => {
    setFormData((f: any) => ({ ...f, items: (f.items || []).filter((i: any) => i.id !== id) }));
  };

  const updateItem = (id: string, field: string, value: any) => {
    setFormData((f: any) => ({
      ...f,
      items: (f.items || []).map((i: any) => i.id === id ? { ...i, [field]: value } : i)
    }));
  };

  const computeItemAmount = (item: { quantity?: number; rateMinor?: number }) => {
    return (Number(item.quantity) || 0) * (Number(item.rateMinor) || 0);
  };

  const subtotal = useMemo(() => 
    (formData.items || []).reduce((sum: number, i: any) => sum + computeItemAmount(i), 0), 
    [formData.items]
  );

  const taxAmount = useMemo(() => Math.round(subtotal * 0.18), [subtotal]);
  const total = subtotal + taxAmount;

  const handleProjectSelect = (projId: string) => {
    const selProject = projects.find((p: any) => (p._id || p.id) === projId);
    if (selProject) {
      const selClient = clients.find((c: any) => (c._id || c.id) === selProject.clientId);
      const resolvedClientName = selProject.clientName || selProject.client || selClient?.name || selClient?.displayName || selClient?.legalName || '';
      setFormData((f: any) => ({
        ...f,
        projectId: projId,
        clientId: selProject.clientId || f.clientId,
        clientName: resolvedClientName || f.clientName,
        contractId: selProject.contractId || f.contractId || '',
        billingType: (['milestone', 'time_material', 'retainer', 'advance', 'recurring', 'final'].includes(selProject.billingMethod) ? selProject.billingMethod : f.billingType) || 'time_material',
      }));
    } else {
      setFormData((f: any) => ({ ...f, projectId: projId }));
    }
  };

  const handleClientSelect = (clientId: string) => {
    const selClient = clients.find((c: any) => (c._id || c.id) === clientId);
    const resolvedClientName = selClient?.name || selClient?.displayName || selClient?.legalName || selClient?.contactId?.displayName || selClient?.contactId || '';
    setFormData((f: any) => ({
      ...f,
      clientId,
      clientName: resolvedClientName || f.clientName,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.clientId) return toast.error('Client is required');
    if (!formData.projectId) return toast.error('Project is required');
    if ((formData.items || []).length === 0) return toast.error('At least one line item required');

    setSaving(true);
    try {
      const selClient = clients.find((c: any) => (c._id || c.id) === formData.clientId);
      const selProject = projects.find((p: any) => (p._id || p.id) === formData.projectId);
      const clientName = selClient?.name || selClient?.displayName || selClient?.legalName || selClient?.contactId?.displayName || selClient?.contactId || selProject?.clientName || selProject?.client || formData.clientName || '';
      const projectName = selProject?.name || formData.projectName || '';

      const payload = {
        ...formData,
        clientName,
        projectName,
        items: (formData.items || []).map((i: any) => ({
          description: i.description,
          quantity: Number(i.quantity) || 1,
          rateMinor: Number(i.rateMinor) || 0,
          amountMinor: computeItemAmount(i),
        })),
        subtotal,
        taxAmount,
        totalAmountMinor: total,
        amountMinor: total,
        invoiceDate: formData.invoiceDate ? new Date(formData.invoiceDate).toISOString() : null,
        dueDate: formData.dueDate ? new Date(formData.dueDate).toISOString() : null,
      };

      if (editing) {
        // Update invoice
        await fetch(`/api/v2/services-projects/billing/${editing._id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(payload),
        });
        toast.success('Invoice updated');
      } else {
        // Create invoice
        await fetch('/api/v2/services-projects/billing', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(payload),
        });
        toast.success('Invoice created');
      }
      queryClient.invalidateQueries({ queryKey: servicesProjectsQueryKeys.billing() });
      resetForm();
    } catch (err) {
      toast.error(errorText(err, 'Failed to save invoice'));
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateFromProject = async (projectData: any) => {
    if (!window.confirm(`Generate invoice for ${projectData.project.name}?`)) return;
    
    setGenerating(true);
    try {
      const rate = projectData.contract ? 'contract' : 'timesheet';
      const response = await fetch('/api/v2/services-projects/billing/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          projectId: projectData.project._id || projectData.project.id,
          contractId: projectData.contract?._id || projectData.contract?.id,
          billingType: rate === 'contract' ? 'milestone' : 'time_material',
          autoPopulate: true,
        }),
      });
      
      if (!response.ok) throw new Error('Failed to generate');
      
      toast.success('Invoice generated successfully');
      queryClient.invalidateQueries({ queryKey: servicesProjectsQueryKeys.billing() });
      setActiveTab('invoices');
    } catch (err) {
      toast.error(errorText(err, 'Failed to generate invoice'));
    } finally {
      setGenerating(false);
    }
  };

  const handleAction = async (id: string, action: string) => {
    try {
      const response = await fetch(`/api/v2/services-projects/billing/${id}/${action}`, {
        method: 'POST',
        credentials: 'include',
      });
      
      if (!response.ok) throw new Error('Failed');
      toast.success(`Invoice ${action}d`);
      queryClient.invalidateQueries({ queryKey: servicesProjectsQueryKeys.billing() });
    } catch (err) {
      toast.error(errorText(err, `Failed to ${action} invoice`));
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this invoice?')) return;
    try {
      await fetch(`/api/v2/services-projects/billing/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      toast.success('Invoice deleted');
      queryClient.invalidateQueries({ queryKey: servicesProjectsQueryKeys.billing() });
    } catch (err) {
      toast.error(errorText(err, 'Failed to delete'));
    }
  };

  const handleSend = async (id: string) => {
    await handleAction(id, 'send');
  };

  const isLoading = invoicesLoading;

  return (
    <div className="space-y-6 pb-16">
      {/* Header */}
      <PageHeader
        icon={Receipt}
        eyebrow="Projects & Services"
        title="Billing"
        description={<>Generate invoices from milestones and approved timesheets</>}
        actions={
          <><div className="flex gap-2">
                  <Button variant="outline" onClick={() => setActiveTab('invoices')}>
                    <FileText className="w-4 h-4 mr-2" /> Invoices
                  </Button>
                  <Button variant="outline" onClick={() => setActiveTab('generate')}>
                    <DollarSign className="w-4 h-4 mr-2" /> Generate
                  </Button>
                  <Button variant="outline" onClick={() => setActiveTab('schedule')}>
                    <Settings className="w-4 h-4 mr-2" /> Schedule
                  </Button>
                  <Button variant="outline" onClick={() => setActiveTab('payments')}>
                    <CreditCard className="w-4 h-4 mr-2" /> Payments
                  </Button>
                  {activeTab === 'invoices' && (
                    <Button onClick={handleCreate}>
                      <Plus className="w-4 h-4 mr-2" /> New Invoice
                    </Button>
                  )}
                </div></>
        }
      />

      {/* Summary Cards */}
      <div className="grid gap-4 sm:grid-cols-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Invoiced</p>
                <p className="text-2xl font-bold">
                  {formatINR(invoices.filter((i: Invoice) => i.status !== 'cancelled').reduce((sum: number, i: Invoice) => sum + (i.amountMinor || 0), 0))}
                </p>
              </div>
              <DollarSign className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Outstanding</p>
                <p className="text-2xl font-bold text-red-600">
                  {formatINR(invoices.filter((i: Invoice) => (i.status ? ['sent', 'partial_paid', 'overdue'].includes(i.status) : false)).reduce((sum: number, i: Invoice) => sum + (i.amountMinor || 0) - (i.paidAmountMinor || 0), 0))}
                </p>
              </div>
              <AlertCircle className="w-8 h-8 text-red-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Paid This Month</p>
                <p className="text-2xl font-bold text-emerald-600">
                  {formatINR(invoices.filter((i: Invoice) => i.status === 'paid' && i.paidAt && new Date(i.paidAt!) > new Date(new Date().getFullYear(), new Date().getMonth(), 1)).reduce((sum: number, i: Invoice) => sum + (i.paidAmountMinor || i.amountMinor || 0), 0))}
                </p>
              </div>
              <CheckCircle className="w-8 h-8 text-emerald-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Overdue Count</p>
                <p className="text-2xl font-bold text-red-600">
                  {formatINR(invoices.filter((i: Invoice) => i.status === 'overdue').length)}
                </p>
              </div>
              <Bell className="w-8 h-8 text-red-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Invoices Tab */}
      {activeTab === 'invoices' && (
        <div className="space-y-4">
          {/* Filters */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-4 items-end">
                <div className="flex-1 min-w-[200px]">
                  <label className="block text-xs font-medium text-gray-500 mb-1">Search</label>
                  <Input placeholder="Search invoice #, client, project..." value={search} onChange={e => setSearch(e.target.value)} leftIcon={<Search className="w-4 h-4" />} />
                </div>
                <div className="min-w-[150px]">
                  <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
                  <Select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} options={[{ value: '', label: 'All' }, ...INVOICE_STATUSES.map(s => ({ value: s.value, label: s.label }))]} />
                </div>
                <div className="min-w-[150px]">
                  <label className="block text-xs font-medium text-gray-500 mb-1">Type</label>
                  <Select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} options={[{ value: '', label: 'All' }, ...BILLING_TYPES.map(t => ({ value: t.value, label: t.label }))]} />
                </div>
                <div className="min-w-[150px]">
                  <label className="block text-xs font-medium text-gray-500 mb-1">Project</label>
                  <Select value={projectFilter} onChange={e => setProjectFilter(e.target.value)} options={[{ value: '', label: 'All' }, ...projects.map((p: any) => ({ value: p._id || p.id, label: p.name }))]} />
                </div>
                <div className="min-w-[150px]">
                  <label className="block text-xs font-medium text-gray-500 mb-1">Client</label>
                  <Select value={clientFilter} onChange={e => setClientFilter(e.target.value)} options={[{ value: '', label: 'All' }, ...clients.map((c: any) => ({ value: c._id || c.id, label: c.name || c.displayName || c.legalName || c.contactId?.displayName || c.contactId?.name || c._id }))]} />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Table */}
          {isLoading ? (
            <Card><CardContent className="py-12 text-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div></CardContent></Card>
          ) : filteredInvoices.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <ReceiptText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No invoices found</h3>
                <p className="text-gray-500 mb-4">Generate invoices from projects or create manually.</p>
                <Button onClick={() => setActiveTab('generate')}>
                  <DollarSign className="w-4 h-4 mr-2" /> Generate Invoice
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Invoice #</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Client</TableHead>
                      <TableHead>Project</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead className="text-right">Paid</TableHead>
                      <TableHead className="text-right">Balance</TableHead>
                      <TableHead>Due Date</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="w-56">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredInvoices.map((inv: Invoice, idx: number) => {
                      const statusConfig = getStatusConfig(inv.status ?? '');
                      const paid = inv.paidAmountMinor || 0;
                      const balance = (inv.amountMinor || 0) - paid;

                      const client = clients.find((c: any) => (c._id || c.id) === (typeof inv.clientId === 'object' ? (inv.clientId as any)?._id : (inv.clientId || (inv as any).client)));
                      const displayClientName = inv.clientName || client?.name || client?.displayName || client?.legalName || client?.contactId?.displayName || client?.contactId?.name || '—';

                      const project = projects.find((p: any) => (p._id || p.id) === (typeof inv.projectId === 'object' ? (inv.projectId as any)?._id : inv.projectId));
                      const displayProjectName = inv.projectName || project?.name || '—';

                      return (
                        <TableRow key={inv._id ?? idx}>
                          <TableCell className="font-mono font-medium">{inv.invoiceNumber || inv._id?.slice(-8)}</TableCell>
                          <TableCell>{inv.invoiceDate ? new Date(inv.invoiceDate).toLocaleDateString() : '—'}</TableCell>
                          <TableCell className="font-medium text-gray-900 dark:text-gray-100">{displayClientName}</TableCell>
                          <TableCell>{displayProjectName}</TableCell>
                          <TableCell><Badge variant="secondary">{inv.billingType?.replace('_', ' ') || '—'}</Badge></TableCell>
                          <TableCell className="text-right font-mono">{formatINR(inv.amountMinor || 0)}</TableCell>
                          <TableCell className="text-right font-mono text-emerald-600">{formatINR(paid)}</TableCell>
                          <TableCell className="text-right font-mono font-medium text-red-600">{formatINR(balance)}</TableCell>
                          <TableCell>{inv.dueDate ? new Date(inv.dueDate).toLocaleDateString() : '—'}</TableCell>
                          <TableCell><Badge className={statusConfig.color}>{statusConfig.label}</Badge></TableCell>
                          <TableCell className="w-56">
                            <div className="flex items-center gap-1">
                              <Button variant="ghost" size="icon" onClick={() => handleView(inv)} title="View"><Eye className="w-4 h-4" /></Button>
                              <Button variant="ghost" size="icon" onClick={() => handleEdit(inv)} title="Edit"><Edit className="w-4 h-4" /></Button>
                              {['draft', 'pending_approval'].includes(inv.status ?? '') && (
                                <Button variant="ghost" size="icon" onClick={() => handleAction(inv._id, 'approve')} title="Approve" className="text-emerald-500"><CheckCircle className="w-4 h-4" /></Button>
                              )}
                              {inv.status === 'approved' && (
                                <Button variant="secondary" size="icon" onClick={() => handleSend(inv._id)} title="Send" className="text-blue-500"><Send className="w-4 h-4" /></Button>
                              )}
                              {['sent', 'partial_paid', 'overdue'].includes(inv.status ?? '') && (
                                <Button variant="ghost" size="icon" onClick={() => handleAction(inv._id, 'mark_paid')} title="Mark Paid" className="text-emerald-500"><Banknote className="w-4 h-4" /></Button>
                              )}
                              <Button variant="ghost" size="icon" onClick={() => handleAction(inv._id, 'download_pdf')} title="PDF"><Download className="w-4 h-4" /></Button>
                              {['draft', 'pending_approval'].includes(inv.status ?? '') && (
                                <Button variant="ghost" size="icon" onClick={() => handleDelete(inv._id)} title="Delete" className="text-red-500"><Trash2 className="w-4 h-4" /></Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </Card>
          )}
        </div>
      )}

      {/* Generate Tab */}
      {activeTab === 'generate' && (
        <div className="space-y-6">
          <Card>
            <CardHeader><CardTitle>Generate Invoices from Projects</CardTitle></CardHeader>
            <CardContent>
              <p className="text-gray-500 mb-4">Select projects with unbilled time or milestone completions to generate invoices.</p>
              
              {billableProjects.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p>No billable projects found. Ensure projects have approved timesheets or active contracts.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {billableProjects.map((bp: any) => (
                    <Card key={bp.project._id} className="border-l-4 border-blue-500">
                      <CardContent className="p-4">
                        <div className="flex flex-wrap items-center justify-between gap-4">
                          <div>
                            <h4 className="font-semibold">{bp.project.name}</h4>
                            <p className="text-sm text-gray-500">{bp.project.client || bp.contract?.clientName || 'No client'}</p>
                          </div>
                          <div className="flex items-center gap-6 text-sm">
                            <div className="text-right"><p className="font-mono font-medium text-emerald-600">{formatINR(bp.totalAmount)}</p><p className="text-xs text-gray-500">Billable Value</p></div>
                            <div className="text-right"><p className="font-mono">{bp.totalHours}h</p><p className="text-xs text-gray-500">Hours</p></div>
                            <div className="text-right"><p className="font-mono text-red-600">{formatINR(Math.max(0, (bp.contract?.totalValueMinor || 0) - bp.billedAmount))}</p><p className="text-xs text-gray-500">Remaining</p></div>
                          </div>
                          <Button 
                            onClick={() => handleGenerateFromProject(bp)} 
                            disabled={generating}
                            variant="secondary"
                          >
                            Generate Invoice
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card>
            <CardHeader><CardTitle>Quick Invoice Creation</CardTitle></CardHeader>
            <CardContent>
              <div className="grid gap-4 sm:grid-cols-3">
                <Button variant="outline" className="h-24 flex flex-col items-center gap-2" onClick={() => { setActiveTab('invoices'); handleCreate(); }}>
                  <FileText className="w-8 h-8" /><span>Manual Invoice</span>
                </Button>
                <Button variant="outline" className="h-24 flex flex-col items-center gap-2">
                  <CreditCard className="w-8 h-8" /><span>Retainer Invoice</span>
                </Button>
                <Button variant="outline" className="h-24 flex flex-col items-center gap-2">
                  <RefreshCw className="w-8 h-8" /><span>Recurring Invoice</span>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Schedule Tab */}
      {activeTab === 'schedule' && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Billing Schedules</CardTitle>
              <Button variant="outline" size="sm"><Plus className="w-4 h-4 mr-2" /> Add Schedule</Button>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-gray-500 mb-4">Manage recurring billing schedules for retainers and subscriptions.</p>
            <div className="text-center py-12 text-gray-500">
              <Settings className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p>Billing schedule management coming soon.</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Payments Tab */}
      {activeTab === 'payments' && (
        <Card>
          <CardHeader>
            <CardTitle>Payment Tracking</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-500 mb-4">Track invoice payments, advances, and adjustments.</p>
            <div className="text-center py-12 text-gray-500">
              <CreditCard className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p>Payment tracking coming soon.</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Create/Edit Modal */}
      <Modal open={creating} onClose={() => { setCreating(false); setEditing(null); }} title={editing ? 'Edit Invoice' : 'New Invoice'} size="lg">
        <form onSubmit={handleSubmit} className="space-y-5 p-6">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Client *</label>
              <Select value={formData.clientId} onChange={e => handleClientSelect(e.target.value)} options={clients.map((c: any) => ({ value: c._id || c.id, label: c.name || c.displayName || c.legalName || c.contactId?.displayName || c.contactId || c._id }))} />
            </div>
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Project *</label>
              <Select value={formData.projectId} onChange={e => handleProjectSelect(e.target.value)} options={projects.map((p: any) => ({ value: p._id || p.id, label: p.name }))} />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Contract</label>
              <Select value={formData.contractId} onChange={e => setFormData({ ...formData, contractId: e.target.value })} options={[{ value: '', label: 'None' }, ...contracts.map(c => ({ value: c._id, label: c.contractNumber }))]} />
            </div>
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Billing Type</label>
              <Select value={formData.billingType} onChange={e => setFormData({ ...formData, billingType: e.target.value })} options={BILLING_TYPES} />
            </div>
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Currency</label>
              <Select value={formData.currency} onChange={e => setFormData({ ...formData, currency: e.target.value })} options={['INR', 'USD', 'EUR', 'GBP'].map(c => ({ value: c, label: c }))} />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Invoice Date</label>
              <Input type="date" value={formData.invoiceDate} onChange={e => setFormData({ ...formData, invoiceDate: e.target.value })} />
            </div>
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Due Date</label>
              <Input type="date" value={formData.dueDate} onChange={e => setFormData({ ...formData, dueDate: e.target.value })} />
            </div>
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Status</label>
              <Select value={formData.status} onChange={e => setFormData({ ...formData, status: e.target.value })} options={INVOICE_STATUSES} />
            </div>
          </div>

          {/* Line Items */}
          <div className="border-t pt-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-medium">Line Items</h3>
              <Button type="button" variant="secondary" size="sm" onClick={addItem}><Plus className="w-4 h-4 mr-1" /> Add Item</Button>
            </div>
            
            {formData.items.length === 0 ? (
              <div className="text-center py-8 text-gray-500 border-2 border-dashed border-gray-200 rounded-lg">
                <p>No line items added. Click "Add Item" to start.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Description</TableHead>
                      <TableHead className="w-20">Qty</TableHead>
                      <TableHead className="w-32">Rate/hr (₹)</TableHead>
                      <TableHead className="w-32">Amount (₹)</TableHead>
                      <TableHead className="w-12"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {formData.items.map((item: any) => (
                      <TableRow key={item.id}>
                        <TableCell>
                          <Input placeholder="Description" value={item.description} onChange={e => updateItem(item.id, 'description', e.target.value)} className="w-full" />
                        </TableCell>
                        <TableCell>
                          <Input type="number" min="0" step="0.5" placeholder="1" value={item.quantity} onChange={e => updateItem(item.id, 'quantity', e.target.value)} className="w-full text-center" />
                        </TableCell>
                        <TableCell>
                          <Input type="number" min="0" step="100" placeholder="0" value={item.rateMinor} onChange={e => updateItem(item.id, 'rateMinor', e.target.value)} className="w-full text-right font-mono" />
                        </TableCell>
                        <TableCell className="font-mono text-right font-medium">{formatINR(computeItemAmount(item))}</TableCell>
                        <TableCell>
                          <Button variant="ghost" size="icon" onClick={() => removeItem(item.id)} className="text-red-500"><Trash2 className="w-4 h-4" /></Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            {/* Totals */}
            <div className="flex justify-end mt-4 space-x-4">
              <div className="w-64 border-t pt-2 space-y-1">
                <div className="flex justify-between text-sm"><span>Subtotal</span><span className="font-mono font-medium">{formatINR(subtotal)}</span></div>
                <div className="flex justify-between text-sm text-gray-500"><span>Tax (18%)</span><span className="font-mono">{formatINR(taxAmount)}</span></div>
                <div className="flex justify-between text-lg font-bold border-t pt-2"><span>Total</span><span className="font-mono">{formatINR(total)}</span></div>
              </div>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Notes</label>
              <Input placeholder="Internal notes" value={formData.notes} onChange={e => setFormData({ ...formData, notes: e.target.value })} />
            </div>
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Terms & Conditions</label>
              <Input placeholder="Payment terms..." value={formData.terms} onChange={e => setFormData({ ...formData, terms: e.target.value })} />
            </div>
          </div>

          <div className="flex justify-end gap-3 border-t pt-4 mt-4">
            <Button type="button" variant="outline" onClick={() => { setCreating(false); setEditing(null); }}>Cancel</Button>
            <Button type="submit" loading={saving}>{editing ? 'Update' : 'Create'} Invoice</Button>
          </div>
        </form>
      </Modal>

      {/* View Modal */}
      <Modal open={!!viewing} onClose={() => setViewing(null)} title="Invoice Details" size="xl">
        {viewing && (() => {
          const viewingClient = clients.find((c: any) => (c._id || c.id) === (typeof viewing.clientId === 'object' ? viewing.clientId?._id : (viewing.clientId || viewing.client)));
          const vClientName = viewing.clientName || viewingClient?.name || viewingClient?.displayName || viewingClient?.legalName || viewingClient?.contactId?.displayName || viewingClient?.contactId?.name || '—';
          
          const viewingProject = projects.find((p: any) => (p._id || p.id) === (typeof viewing.projectId === 'object' ? viewing.projectId?._id : viewing.projectId));
          const vProjectName = viewing.projectName || viewingProject?.name || '—';

          return (
            <div className="p-6 space-y-6">
              <div className="grid gap-4 sm:grid-cols-3 bg-slate-50 dark:bg-slate-900 p-4 rounded-lg border border-border">
                <div><span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Invoice #</span><p className="font-mono font-bold text-lg">{viewing.invoiceNumber || viewing._id?.slice(-8)}</p></div>
                <div><span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Date</span><p className="font-medium">{viewing.invoiceDate ? new Date(viewing.invoiceDate).toLocaleDateString() : '—'}</p></div>
                <div><span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Due Date</span><p className="font-medium">{viewing.dueDate ? new Date(viewing.dueDate).toLocaleDateString() : '—'}</p></div>
                <div><span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Client</span><p className="font-semibold text-gray-900 dark:text-white">{vClientName}</p></div>
                <div><span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Project</span><p className="font-medium">{vProjectName}</p></div>
                <div><span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Type</span><p><Badge variant="secondary">{viewing.billingType?.replace('_', ' ') || '—'}</Badge></p></div>
                <div><span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</span><p><Badge className={getStatusConfig(viewing.status).color}>{getStatusConfig(viewing.status).label}</Badge></p></div>
                <div><span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Total Amount</span><p className="font-bold text-xl text-blue-600">{formatINR(viewing.totalAmountMinor || viewing.amountMinor || 0)}</p></div>
                <div><span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Paid Amount</span><p className="font-bold text-xl text-emerald-600">{formatINR(viewing.paidAmountMinor || 0)}</p></div>
              </div>
              
              {viewing.items && viewing.items.length > 0 && (
                <div className="border-t border-border pt-4">
                  <h4 className="font-semibold text-body-md mb-3">Line Items</h4>
                  <div className="overflow-x-auto rounded-lg border border-border">
                    <Table>
                      <TableHeader><TableRow><TableHead>Description</TableHead><TableHead className="w-20 text-center">Qty</TableHead><TableHead className="w-32 text-right">Rate</TableHead><TableHead className="w-32 text-right">Amount</TableHead></TableRow></TableHeader>
                      <TableBody>
                        {viewing.items.map((item: any, i: number) => (
                          <TableRow key={item.id || i}><TableCell className="font-medium">{item.description}</TableCell><TableCell className="text-center font-mono">{item.quantity}</TableCell><TableCell className="font-mono text-right">{formatINR(item.rateMinor || 0)}</TableCell><TableCell className="font-mono text-right font-medium">{formatINR(item.amountMinor || 0)}</TableCell></TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
              
              {viewing.notes && (
                <div className="border-t border-border pt-4">
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Notes</span>
                  <p className="mt-1 text-sm text-gray-700 dark:text-gray-300 bg-slate-50 dark:bg-slate-900 p-3 rounded-lg border border-border whitespace-pre-wrap">{viewing.notes}</p>
                </div>
              )}
            </div>
          );
        })()}
      </Modal>
    </div>
  );
}